import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, ChevronRight, ChevronDown, Layers, Tag, Pencil, Package } from 'lucide-react';
import { erpList, erpCreate, erpUpdate } from '../../../services/erpApi';
import { Card, CardHeader, Button, Input, Textarea, PageHeader, Table, FormModal, EmptyState, Badge, SearchBox, fmtInr } from '../ui';

const LEVELS = [
  { key: 'vertical', path: 'verticals', child: 'category', label: 'Vertical' },
  { key: 'category', path: 'product-categories', parentField: 'vertical_id', child: 'subcategory', label: 'Category' },
  { key: 'subcategory', path: 'subcategories', parentField: 'category_id', child: 'family', label: 'Subcategory' },
  { key: 'family', path: 'families', parentField: 'subcategory_id', child: null, label: 'Family' },
];
const LVL = Object.fromEntries(LEVELS.map((l) => [l.key, l]));

const TreeCtx = createContext({});

const TreeNode = ({ node, level, depth }) => {
  const { tree, selected, onSelect, onAdd, loadKids } = useContext(TreeCtx);
  const kids = tree[`${level}:${node.id}`];
  const [open, setOpen] = useState(depth === 0);
  const lvl = LVL[level];
  const isSel = selected?.id === node.id && selected?.level === level;
  useEffect(() => { if (open && lvl.child && kids === undefined) loadKids(level, node.id); }, [open, lvl.child, kids, loadKids, level, node.id]);
  return (
    <div>
      <div className={`group flex items-center gap-1 rounded-lg pr-1 transition-colors ${isSel ? 'bg-[var(--erp-blue-soft)]' : 'hover:bg-[#f5f7fb]'}`} style={{ paddingLeft: depth * 14 }}>
        <button type="button" onClick={() => setOpen((o) => !o)} className="h-8 w-7 grid place-items-center flex-shrink-0" style={{ color: 'var(--erp-muted)', visibility: lvl.child ? 'visible' : 'hidden' }} data-testid={`tree-toggle-${node.id}`}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <button type="button" onClick={() => onSelect({ level, id: node.id, node })} data-testid={`tree-${level}-${node.id}`}
          className={`flex-1 text-left h-8 text-sm truncate ${isSel ? 'font-bold' : 'font-medium'}`} style={{ color: isSel ? 'var(--erp-blue)' : 'var(--erp-ink)' }}>
          {node.name}{node.code && <span className="ml-1.5 text-[11px] font-normal" style={{ color: 'var(--erp-muted)' }}>{node.code}</span>}
        </button>
        {lvl.child && (
          <button type="button" onClick={() => onAdd(lvl.child, node, level)} title={`Add ${LVL[lvl.child].label}`} data-testid={`tree-add-${lvl.child}-${node.id}`}
            className={`h-7 w-7 rounded-md grid place-items-center hover:bg-white transition-all ${isSel ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} style={{ color: 'var(--erp-blue)' }}><Plus size={14} /></button>
        )}
      </div>
      {open && lvl.child && (
        <div>
          {(kids || []).map((k) => React.createElement(TreeNode, { key: k.id, node: k, level: lvl.child, depth: depth + 1 }))}
          {kids && kids.length === 0 && <p className="text-[11.5px] py-1" style={{ paddingLeft: (depth + 1) * 14 + 28, color: '#a3abbf' }}>No {LVL[lvl.child].label.toLowerCase()} yet</p>}
        </div>
      )}
    </div>
  );
};

const EMPTY_SKU = { name: '', code: '', uom: 'NOS', list_price: '', hsn_code: '', description: '' };

export const Products = () => {
  const [tree, setTree] = useState({});
  const [roots, setRoots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [skus, setSkus] = useState([]);
  const [q, setQ] = useState('');
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [nodeModal, setNodeModal] = useState(null);
  const [nodeName, setNodeName] = useState('');
  const [skuModal, setSkuModal] = useState(null);
  const [skuForm, setSkuForm] = useState(EMPTY_SKU);
  const [busy, setBusy] = useState(false);

  const loadKids = useCallback(async (level, id) => {
    const child = LVL[LVL[level].child];
    const r = await erpList(child.path, { parent: id });
    setTree((t) => ({ ...t, [`${level}:${id}`]: r.data.items || [] }));
  }, []);
  useEffect(() => { erpList('verticals').then((r) => setRoots(r.data.items || [])); }, []);

  const familyId = selected?.level === 'family' ? selected.id : null;
  const loadSkus = useCallback(async () => {
    setLoadingSkus(true);
    try { const r = await erpList('skus', familyId ? { family_id: familyId, q } : { q }); setSkus(r.data.items || []); }
    finally { setLoadingSkus(false); }
  }, [familyId, q]);
  useEffect(() => { const t = setTimeout(loadSkus, 200); return () => clearTimeout(t); }, [loadSkus]);

  const saveNode = async () => {
    const { level, parent, parentLevel } = nodeModal;
    setBusy(true);
    try {
      await erpCreate(LVL[level].path, { name: nodeName, [LVL[level].parentField]: parent.id });
      toast.success(`${LVL[level].label} added`); setNodeModal(null); setNodeName('');
      loadKids(parentLevel, parent.id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const verticalOf = useMemo(() => {
    // walk selected chain up to the vertical via tree keys
    if (!selected) return null;
    if (selected.level === 'vertical') return selected.id;
    const find = (lvl, id) => {
      const parentLvl = LEVELS[LEVELS.findIndex((l) => l.key === lvl) - 1];
      if (!parentLvl) return id;
      const entry = Object.entries(tree).find(([k, v]) => k.startsWith(`${parentLvl.key}:`) && v.some((n) => n.id === id));
      return entry ? find(parentLvl.key, entry[0].split(':')[1]) : null;
    };
    return find(selected.level, selected.id);
  }, [selected, tree]);

  const saveSku = async () => {
    setBusy(true);
    try {
      const body = { ...skuForm, list_price: parseFloat(skuForm.list_price) || 0 };
      if (skuModal.id) await erpUpdate('skus', skuModal.id, body);
      else await erpCreate('skus', { ...body, family_id: familyId, vertical_id: verticalOf });
      toast.success(skuModal.id ? 'SKU updated' : 'SKU created'); setSkuModal(null); loadSkus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const openSku = (s) => { setSkuForm(s ? { name: s.name, code: s.code, uom: s.uom, list_price: s.list_price, hsn_code: s.hsn_code || '', description: s.description || '' } : EMPTY_SKU); setSkuModal(s || {}); };
  const setS = (k) => (e) => setSkuForm((f) => ({ ...f, [k]: e.target.value }));

  const columns = [
    { key: 'code', label: 'Code', render: (s) => <span className="font-mono text-xs font-semibold px-2 py-1 rounded-md" style={{ background: '#f3f6fb' }}>{s.code}</span> },
    { key: 'name', label: 'SKU', render: (s) => <div><p className="font-semibold">{s.name}</p>{s.description && <p className="text-xs truncate max-w-xs" style={{ color: 'var(--erp-muted)' }}>{s.description}</p>}</div> },
    { key: 'hsn_code', label: 'HSN', render: (s) => s.hsn_code || '—' },
    { key: 'uom', label: 'UOM' },
    { key: 'list_price', label: 'List price', align: 'right', render: (s) => <span className="font-bold">{fmtInr(s.list_price)}</span> },
    { key: 'is_active', label: 'Status', render: (s) => <Badge tone={s.is_active === false ? 'gray' : 'green'}>{s.is_active === false ? 'Inactive' : 'Active'}</Badge> },
    { key: 'edit', label: '', align: 'right', render: (s) => <Button variant="ghost" size="sm" icon={Pencil} onClick={(e) => { e.stopPropagation(); openSku(s); }} data-testid={`sku-edit-${s.id}`} /> },
  ];

  return (
    <div data-testid="erp-products">
      <PageHeader crumb="Masters" title="Product Master" subtitle="Vertical → Category → Subcategory → Family → SKU. Select a family to add SKUs."
        actions={<Button icon={Plus} disabled={!familyId} onClick={() => openSku(null)} data-testid="sku-add" title={familyId ? '' : 'Select a family first'}>New SKU</Button>} />
      <div className="grid lg:grid-cols-[320px_1fr] gap-4 items-start">
        <Card className="p-0 erp-rise erp-d1 lg:sticky lg:top-28">
          <CardHeader icon={Layers} title="Hierarchy" subtitle="Hover a node to add a child" />
          <div className="px-3 pb-3 max-h-[60vh] overflow-y-auto erp-scroll">
            <TreeCtx.Provider value={{ tree, selected, onSelect: setSelected, loadKids, onAdd: (level, parent, parentLevel) => setNodeModal({ level, parent, parentLevel }) }}>
              {roots.map((v) => <TreeNode key={v.id} node={v} level="vertical" depth={0} />)}
            </TreeCtx.Provider>
          </div>
        </Card>
        <Card className="p-0 erp-rise erp-d2">
          <div className="flex flex-wrap items-center gap-3 px-5 pt-5 pb-3">
            <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: 'var(--erp-blue-soft)', color: 'var(--erp-blue)' }}><Tag size={16} /></span>
            <div className="flex-1 min-w-[160px]">
              <h3 className="text-[15px] font-bold leading-tight">{familyId ? selected.node.name : 'All SKUs'}</h3>
              <p className="text-xs" style={{ color: 'var(--erp-muted)' }}>{skus.length} SKU{skus.length === 1 ? '' : 's'}{familyId ? ' in this family' : ' across all families'}</p>
            </div>
            <SearchBox value={q} onChange={setQ} placeholder="Search code, name, HSN…" className="w-64" data-testid="sku-search" />
          </div>
          <Table testid="sku-table" columns={columns} rows={skus} loading={loadingSkus} onRowClick={openSku}
            empty={<EmptyState icon={Package} title={familyId ? 'No SKUs in this family' : 'No SKUs found'} text={familyId ? 'Create the first SKU for this family.' : 'Select a family in the hierarchy to add SKUs.'}
              action={familyId && <Button icon={Plus} onClick={() => openSku(null)}>New SKU</Button>} />} />
        </Card>
      </div>

      <FormModal open={!!nodeModal} onClose={() => setNodeModal(null)} onSubmit={saveNode} busy={busy} testid="node-modal" width="max-w-md"
        title={`New ${nodeModal ? LVL[nodeModal.level].label : ''}`} subtitle={nodeModal ? `Under ${nodeModal.parent.name}` : ''}>
        <Input label="Name" required autoFocus value={nodeName} onChange={(e) => setNodeName(e.target.value)} placeholder="e.g. Carbide Inserts" data-testid="node-name" />
      </FormModal>

      <FormModal open={!!skuModal} onClose={() => setSkuModal(null)} onSubmit={saveSku} busy={busy} testid="sku-modal" submitLabel={skuModal?.id ? 'Update SKU' : 'Create SKU'}
        title={skuModal?.id ? 'Edit SKU' : 'New SKU'} subtitle={skuModal?.id ? skuModal.code : `Family: ${selected?.node?.name || ''}`}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="SKU name" required className="sm:col-span-2" value={skuForm.name} onChange={setS('name')} placeholder="End Mill 10mm 4F Carbide" data-testid="sku-name" />
          <Input label="SKU code" required value={skuForm.code} onChange={setS('code')} disabled={!!skuModal?.id} placeholder="EM-10-4F" data-testid="sku-code" hint={skuModal?.id ? 'Code cannot be changed' : 'Must be unique'} />
          <Input label="HSN code" value={skuForm.hsn_code} onChange={setS('hsn_code')} placeholder="8207" data-testid="sku-hsn" />
          <Input label="Unit of measure" value={skuForm.uom} onChange={setS('uom')} placeholder="NOS" data-testid="sku-uom" />
          <Input label="List price (₹)" type="number" min="0" step="0.01" value={skuForm.list_price} onChange={setS('list_price')} placeholder="0.00" data-testid="sku-price" />
          <Textarea label="Description" className="sm:col-span-2" value={skuForm.description} onChange={setS('description')} placeholder="Optional specification notes" data-testid="sku-description" />
        </div>
      </FormModal>
    </div>
  );
};
